import { useState, useRef, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { GrNext, GrPrevious } from "react-icons/gr";
import { FaHeart, FaRegHeart, FaTrash } from "react-icons/fa";
import { normalizeBodyPart, getAllBodyParts } from "../../utils/bodyPartNormalizer";
import { API_BASE, BACKEND_BASE } from "../../utils/Instance";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner"; 

function SampleNextArrow({ onClick }) {
  return (
    <div
      onClick={onClick}
      className="absolute right-[0px] top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 cursor-pointer hover:border-gray-500"
    >
      <GrNext size={20} className="text-[#777]" />
    </div>
  );
}

function SamplePrevArrow({ onClick, currentSlide }) {
  if (currentSlide === 0) return null;
  return (
    <div
      onClick={onClick}
      className="absolute left-[-10px] top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full border border-gray-300 cursor-pointer hover:border-gray-500"
    >
      <GrPrevious size={20} className="text-[#777]" />
    </div>
  );
}

const popularFilters = getAllBodyParts();

function GallerySlider({ gallery, setGallery }) {
  const sliderRef = useRef(null);
  const filterSliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeFilter, setActiveFilter] = useState("All Photos");
  const [isMobile, setIsMobile] = useState(false);
  const [updatingProfilePic, setUpdatingProfilePic] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const { profile, setProfile } = useAuth();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    rows: 1,
    slidesPerRow: 1,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          arrows: true,
          dots: true
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: true,
          dots: true
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
          dots: true
        }
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true
        }
      }
    ]
  };

  const filterSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: isMobile ? 2 : 3,
    slidesToScroll: 1,
    variableWidth: true,
    arrows: false,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          variableWidth: true
        }
      }
    ]
  };

  const setSelection = async (displayIndex) => {
    // Find the actual item in the full gallery (not filtered)
    const displayedItems = activeFilter === "All Photos" 
      ? gallery 
      : gallery.filter(item => normalizeBodyPart(item.bodyPart) === activeFilter);
    
    const clickedItem = displayedItems[displayIndex];
    if (!clickedItem) return;
    
    // Prevent changing selection if image is in a contest
    if (clickedItem.is_in_contest) {
      toast.info("This image is active in a contest and cannot be changed.");
      return;
    }
    
    const clickedBodyPart = clickedItem.bodyPart;
    const newSelectionState = !clickedItem.selection;
    
    // Special handling for Profile Picture category
    if (clickedBodyPart === "Profile Picture") {
      if (newSelectionState === true) {
        // User wants to set this as profile picture
        await handleSetProfilePicture(clickedItem);
      }
      return; // Don't use auto-entry logic for profile pictures
    }
    
    // Regular auto-entry logic for other categories
    setGallery((prev) => {
      // If turning ON auto-enter for this image:
      // Turn OFF auto-enter for all other images in the SAME category
      return prev.map((item) => {
        if (item.id === clickedItem.id) {
          // Toggle the clicked item
          return { ...item, selection: newSelectionState };
        } else if (item.bodyPart === clickedBodyPart && newSelectionState === true) {
          // Turn off other images in same category when enabling this one
          return { ...item, selection: false };
        }
        return item;
      });
    });
  };

  const handleSetProfilePicture = async (imageItem) => {
    if (updatingProfilePic) return; // Prevent double-clicking
    
    try {
      setUpdatingProfilePic(true);
      
      if (!profile?.accessToken) {
        toast.error("Please login to update profile picture");
        return;
      }

      console.log('🖼️ Setting profile picture from gallery image:', imageItem.id);

      // Send request to update profile picture
      const response = await fetch(`${API_BASE}accounts/profile/me/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${profile.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profile_picture_from_gallery: imageItem.id // Send body_part_image ID
        }),
      });

      if (response.ok) {
        const updatedProfile = await response.json();
        console.log('✅ Profile updated from backend:', updatedProfile);
        
        // Build full URL for profile picture
        const profilePicUrl = updatedProfile.profile_picture
          ? (updatedProfile.profile_picture.startsWith("http")
              ? updatedProfile.profile_picture
              : `${BACKEND_BASE}${updatedProfile.profile_picture}`)
          : null;
        
        console.log('🖼️ New profile picture URL:', profilePicUrl);
        console.log('🖼️ Setting image ID as active:', imageItem.id);
        
        // First update gallery state immediately
        setGallery(prev => {
          const updated = prev.map(item => {
            // For Profile Picture category, only mark this image as selected
            if (item.bodyPart === "Profile Picture") {
              return {
                ...item,
                selection: item.id === imageItem.id
              };
            }
            // Keep other categories unchanged
            return item;
          });
          
          const profilePicItems = updated.filter(i => i.bodyPart === "Profile Picture");
          console.log('🔄 Gallery state updated. Profile Picture items:', profilePicItems.map(i => ({
            id: i.id,
            selection: i.selection,
            image: i.image
          })));
          
          return updated;
        });
        
        // Then update auth context with new profile picture
        setProfile(prev => ({
          ...prev,
          profilePicture: profilePicUrl,
          profile_picture: profilePicUrl
        }));
        
        toast.success("✅ Profile picture updated successfully!");
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to update profile picture:', errorData);
        toast.error(`Failed to update profile picture: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error updating profile picture:', error);
      toast.error('Failed to update profile picture');
    } finally {
      setUpdatingProfilePic(false);
    }
  };

  const handleDeleteImage = async (imageItem, displayIndex) => {
    if (deletingImageId === imageItem.id) return; // Prevent double-clicking
    
    // Show confirmation dialog
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this ${imageItem.bodyPart} image? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;
    
    try {
      setDeletingImageId(imageItem.id);
      
      if (!profile?.accessToken) {
        toast.error("Please login to delete images");
        return;
      }

      console.log('🗑️ Deleting image:', imageItem.id);

      // Send request to delete image
      const response = await fetch(`${API_BASE}accounts/body-part-images/${imageItem.id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${profile.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        console.log('✅ Image deleted successfully');
        
        // Remove the image from gallery state
        setGallery(prev => prev.filter(item => item.id !== imageItem.id));
        
        // If this was a profile picture, we might need to update profile context
        if (imageItem.bodyPart === "Profile Picture" && imageItem.selection) {
          // Check if there are other profile pictures to set as active
          const remainingProfilePics = gallery.filter(
            item => item.id !== imageItem.id && item.bodyPart === "Profile Picture"
          );
          
          if (remainingProfilePics.length === 0) {
            // No more profile pictures, clear the profile picture
            setProfile(prev => ({
              ...prev,
              profilePicture: null,
              profile_picture: null
            }));
          }
        }
        
        toast.success("✅ Image deleted successfully!");
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ Failed to delete image:', errorData);
        toast.error(`Failed to delete image: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error deleting image:', error);
      toast.error('Failed to delete image');
    } finally {
      setDeletingImageId(null);
    }
  };

  const toggleLike = (index) => {
    setGallery((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, liked: !item.liked } : item
      )
    );
  };

  const filteredGallery =
    activeFilter === "All Photos"
      ? gallery
      : gallery.filter((item) => normalizeBodyPart(item.bodyPart) === activeFilter);

  // Debug logging (can be removed in production)
  console.log("Active filter:", activeFilter);
  console.log("All gallery items count:", gallery.length);
  console.log("Filtered gallery count:", filteredGallery.length);
  console.log("Filtered gallery items:", filteredGallery.map(item => ({ 
    id: item.id, 
    bodyPart: item.bodyPart, 
    selection: item.selection 
  })));

  return (
    <>
      <style jsx>{`
        .slider-badge-container {
          width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
        }
        .slider-badge-container .slick-slide {
          padding: 0 12px;
        }
        .slider-badge-container .slick-list {
          margin: 0 -12px;
          overflow: visible;
        }
        .slider-badge-container .slick-track {
          display: flex;
          align-items: stretch;
        }
        .slider-badge-container .slick-slide > div {
          height: 100%;
        }
        .slider-badge-container .slick-arrow {
          z-index: 10;
        }
        .slider-badge-container .slick-prev {
          left: -40px;
        }
        .slider-badge-container .slick-next {
          right: -40px;
        }
        .slider-badge-container .slick-dots {
          bottom: -40px;
        }
        /* Custom scrollbar */
        .slider-badge-container::-webkit-scrollbar {
          height: 8px;
        }
        .slider-badge-container::-webkit-scrollbar-track {
          background: #1C1C1E;
          border-radius: 4px;
        }
        .slider-badge-container::-webkit-scrollbar-thumb {
          background: #CEB46A;
          border-radius: 4px;
        }
        .slider-badge-container::-webkit-scrollbar-thumb:hover {
          background: #d6c074;
        }
      `}</style>
      <div className="w-full pb-10">
      {/* Filter Slider */}
      <div className="relative flex flex-col sm:flex-row sm:items-center mb-4 gap-2">
        <button className="flex items-center px-4 py-2 mr-2">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 6H21M7 12H17M10 18H14"
              stroke="#CEB46A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="ml-2 font-medium text-[#CEB46A]">Filters</span>
        </button>

        <div className="relative slider-container w-full max-w-screen-lg px-4 sm:px-10">
          {!isMobile && (
            <>
              <SamplePrevArrow
                onClick={() => filterSliderRef.current?.slickPrev()}
                currentSlide={currentSlide}
              />
              <SampleNextArrow
                onClick={() => filterSliderRef.current?.slickNext()}
              />
            </>
          )}
          <Slider ref={filterSliderRef} {...filterSettings}>
            {popularFilters.map((filter) => (
              <div key={filter} className="pr-2">
                <button
                  className={`px-4 py-2 rounded-full whitespace-nowrap text-sm ${
                    activeFilter === filter
                      ? "bg-[#CEB46A] text-black"
                      : "bg-[#1C1C1E] text-white"
                  }`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter}
                </button>
              </div>
            ))}
          </Slider>
        </div>
      </div>

      {/* Image Gallery */}
      {filteredGallery.length > 0 ? (
        <div className="w-full">
          {/* Responsive Grid Layout for Mobile */}
          <div className="block md:hidden">
            <div className="grid grid-cols-1 gap-4 px-4">
              {filteredGallery.map((item, index) => {
                // Log Profile Picture items for debugging
                if (item.bodyPart === "Profile Picture") {
                  console.log(`🖼️ Rendering Profile Picture ${item.id}:`, {
                    selection: item.selection,
                    buttonText: item.selection ? '✓ Active' : 'Set'
                  });
                }
                
                return (
                <div key={`${item.id}-${item.bodyPart}-${index}`} className="relative bg-[#1C1C1E] rounded-[15px] overflow-hidden">
                  {/* Delete Icon */}
                  <div
                    className="absolute top-2 left-2 z-10 cursor-pointer bg-red-600 hover:bg-red-700 rounded-full p-2 transition-colors"
                    onClick={() => handleDeleteImage(item, index)}
                    title="Delete image"
                  >
                    {deletingImageId === item.id ? (
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <FaTrash className="text-white text-sm" />
                    )}
                  </div>

                  {/* Heart Icon */}
                  <div
                    className="absolute top-2 right-2 z-10 text-xl cursor-pointer text-[#CEB46A]"
                    onClick={() => toggleLike(index)}
                  >
                    {item.liked ? <FaHeart /> : <FaRegHeart />}
                  </div>

                  {/* Image */}
                  <img
                    src={item.image}
                    alt={`Gallery item ${index}`}
                    className="w-full h-[250px] object-cover rounded-t-[15px]"
                    loading="lazy"
                  />

                  {/* Info Bar */}
                  <div className="bg-[#1C1C1E] flex justify-between items-center px-[15px] sm:px-[20px] py-[10px]">
                    <span className="text-sm sm:text-md truncate pr-2 text-[#CEB46A]">
                      {item.bodyPart}
                    </span>
                    <div className="flex items-center gap-2">
                      {item.bodyPart === "Profile Picture" ? (
                        <>
                          <span className={`text-xs ${item.selection ? 'text-[#CEB46A] font-semibold' : 'text-white'}`}>
                            {item.selection ? 'Active Profile' : 'Use as Profile'}
                          </span>
                          <button
                            onClick={() => {
                              console.log('🔘 Profile Picture button clicked (mobile):', {
                                imageId: item.id,
                                currentSelection: item.selection,
                                imageUrl: item.image
                              });
                              setSelection(index);
                            }}
                            disabled={updatingProfilePic || item.selection}
                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                              item.selection 
                                ? 'bg-[#CEB46A] text-black cursor-default' 
                                : 'bg-gray-700 text-white hover:bg-[#CEB46A] hover:text-black'
                            } ${updatingProfilePic ? 'opacity-50 cursor-wait' : ''}`}
                          >
                            {updatingProfilePic ? '...' : item.selection ? '✓ Active' : 'Set'}
                          </button>
                        </>
                      ) : (
                        <>
                          <span className={`text-xs ${(item.selection || item.is_in_contest) ? 'text-[#CEB46A] font-semibold' : 'text-white'}`}>
                            {item.is_in_contest ? 'Active' : (item.selection ? 'Auto-Entry ON' : 'Auto Enter')}
                          </span>
                          {item.is_in_contest ? (
                            <button
                              disabled
                              className="px-3 py-1 rounded-full text-xs font-semibold bg-[#CEB46A] text-black cursor-default"
                            >
                              Active
                            </button>
                          ) : (
                            <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={item.selection}
                                onChange={() => setSelection(index)}
                              />
                              <div
                                className={`relative w-10 h-6 ${
                                  item.selection ? "bg-amber-00" : "bg-gray-200"
                                } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-500 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-gray-200 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#CEB46A]`}
                              ></div>
                            </label>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Slider Layout for Desktop */}
          <div className="hidden md:block relative slider-badge-container w-full px-4 sm:px-6 md:px-8 lg:px-12">
            <Slider ref={sliderRef} {...settings}>
              {filteredGallery.map((item, index) => {
                // Log Profile Picture items for debugging
                if (item.bodyPart === "Profile Picture") {
                  console.log(`🖼️ Rendering Profile Picture ${item.id} (desktop):`, {
                    selection: item.selection,
                    buttonText: item.selection ? '✓ Active' : 'Set'
                  });
                }
                
                return (
                <div key={`${item.id}-${item.bodyPart}-${index}`} className="px-1 sm:px-2">
                  <div className="relative bg-[#1C1C1E] rounded-[15px] overflow-hidden">
                    {/* Delete Icon */}
                    <div
                      className="absolute top-2 left-2 z-10 cursor-pointer bg-red-600 hover:bg-red-700 rounded-full p-2 transition-colors"
                      onClick={() => handleDeleteImage(item, index)}
                      title="Delete image"
                    >
                      {deletingImageId === item.id ? (
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                      ) : (
                        <FaTrash className="text-white text-sm" />
                      )}
                    </div>

                    {/* Heart Icon */}
                    <div
                      className="absolute top-2 right-2 z-10 text-xl cursor-pointer text-[#CEB46A]"
                      onClick={() => toggleLike(index)}
                    >
                      {item.liked ? <FaHeart /> : <FaRegHeart />}
                    </div>

                    {/* Image */}
                    <img
                      src={item.image}
                      alt={`Gallery item ${index}`}
                      className="w-full h-[200px] sm:h-[230px] object-cover rounded-t-[15px]"
                      loading="lazy"
                    />

                    {/* Info Bar */}
                    <div className="bg-[#1C1C1E] flex justify-between items-center px-[15px] sm:px-[20px] py-[10px]">
                      <span className="text-sm sm:text-md truncate pr-2 text-[#CEB46A]">
                        {item.bodyPart}
                      </span>
                      <div className="flex items-center gap-2">
                        {item.bodyPart === "Profile Picture" ? (
                          <>
                            <span className={`text-xs ${item.selection ? 'text-[#CEB46A] font-semibold' : 'text-white'}`}>
                              {item.selection ? 'Active Profile' : 'Use as Profile'}
                            </span>
                            <button
                              onClick={() => {
                                console.log('🔘 Profile Picture button clicked (desktop):', {
                                  imageId: item.id,
                                  currentSelection: item.selection,
                                  imageUrl: item.image
                                });
                                setSelection(index);
                              }}
                              disabled={updatingProfilePic || item.selection}
                              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                                item.selection 
                                  ? 'bg-[#CEB46A] text-black cursor-default' 
                                  : 'bg-gray-700 text-white hover:bg-[#CEB46A] hover:text-black'
                              } ${updatingProfilePic ? 'opacity-50 cursor-wait' : ''}`}
                            >
                              {updatingProfilePic ? '...' : item.selection ? '✓ Active' : 'Set'}
                            </button>
                          </>
                        ) : (
                          <>
                            <span className={`text-xs ${(item.selection || item.is_in_contest) ? 'text-[#CEB46A] font-semibold' : 'text-white'}`}>
                              {item.is_in_contest ? 'Active' : (item.selection ? 'Auto-Entry ON' : 'Auto Enter')}
                            </span>
                            {item.is_in_contest ? (
                              <button
                                disabled
                                className="px-3 py-1 rounded-full text-xs font-semibold bg-[#CEB46A] text-black cursor-default"
                              >
                                Active
                              </button>
                            ) : (
                              <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                                <input
                                  type="checkbox"
                                  className="sr-only peer"
                                  checked={item.selection}
                                  onChange={() => setSelection(index)}
                                />
                                <div
                                  className={`relative w-10 h-6 ${
                                    item.selection ? "bg-amber-00" : "bg-gray-200"
                                  } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-500 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-gray-200 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#CEB46A]`}
                                ></div>
                              </label>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </Slider>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-400">
          <p>No images found for this category.</p>
        </div>
      )}
      </div>
    </>
  );
}

export default GallerySlider;
